import { ImageType, ProductDataType } from 'utils/types';
import { Section } from 'utils/types/contentfulTypes';

import { BannerAndCardsV2Props } from './BannerAndCardsV2';

type SubsectionProductContext = {
  products: {
    slug: string;
    creditCost: number;
  }[];
};

type HorizontalProductCardInfo = {
  displayName: string;
  creditCost: number;
  dollarAmount: number;
  centsAmount: number;
  image: ImageType | undefined;
  linkTo: string;
};

export type HorizontalProductCardSubsection = {
  displayName: string;
  products: HorizontalProductCardInfo[];
};

// Type guard function to check if the subsection is a product context
const isSubsectionProductContext = (
  value: any,
): value is SubsectionProductContext =>
  typeof value === 'object' &&
  value.products &&
  Array.isArray(value.products) &&
  value.products.every(
    (product: any) =>
      typeof product === 'object' &&
      typeof product.slug === 'string' &&
      typeof product.creditCost === 'number',
  );

// Parse each to a type to be use on the horizontal card
const parseProductCards = (
  section: Section,
  productsWithSpreeData: Record<string, ProductDataType>,
) => {
  if (!section || !section.subsections || section.subsections.length === 0) {
    return [];
  }

  const subsection = section.subsections[0];

  if (
    !subsection ||
    !subsection.metadata ||
    !isSubsectionProductContext(subsection.metadata.context)
  ) {
    return [];
  }

  const { products } = subsection.metadata.context as SubsectionProductContext;

  const horizontalCards: HorizontalProductCardInfo[] = [];

  products.forEach(({ slug, creditCost }) => {
    const product =
      productsWithSpreeData && slug && productsWithSpreeData[slug];

    if (!product) {
      return null;
    }

    const { price, displayName, productCardImage } = product;

    const dollarAmount = price ? Math.floor(price) : 0;

    const horizontalCard: HorizontalProductCardInfo = {
      displayName: displayName,
      creditCost: creditCost,
      dollarAmount: dollarAmount,
      centsAmount: price ? Math.round((price - Number(dollarAmount)) * 100) : 0,
      image: productCardImage,
      linkTo: `/products/${slug}`,
    };

    horizontalCards.push(horizontalCard);
  });

  return horizontalCards;
};

const buildProductSubsection = (
  section: Section,
  productsWithSpreeData: Record<string, ProductDataType>,
): HorizontalProductCardSubsection | undefined => {
  // Check if the section and subsections are defined
  if (!section || !section.subsections) {
    return; // Return undefined or handle the case where section or subsections are not defined
  }

  // Check if subsections exist and have product cards
  const hasProductCards =
    section.subsections.length > 0 &&
    section.subsections[0]?.componentName === 'HorizontalProductCards' &&
    isSubsectionProductContext(section.subsections[0]?.metadata?.context);

  if (!hasProductCards) {
    return;
  }

  const horizontalCards = parseProductCards(section, productsWithSpreeData);

  return {
    displayName: section.subsections[0]?.displayName || '',
    products: horizontalCards,
  };
};

export const parseBannerAndCardsV2Data = (
  contentfulSection: Section,
  productsWithSpreeData: Record<string, ProductDataType>,
): BannerAndCardsV2Props | null => {
  const { body, images, eyebrow } = contentfulSection;

  const { title, theme, flags } = contentfulSection.metadata || {};

  const backgroundColor = theme?.backgroundColor;
  const desktopBackgroundImage = images && images[0].desktopImage;
  const desktopBackgroundImageAltText = images && images[0].desktopImageAltText;

  const BannerAndCardsV2Props: BannerAndCardsV2Props = {
    eyebrow,
    title,
    bgColor: backgroundColor,
    flags,
    headline: body,
    desktopBackgroundImage: desktopBackgroundImage,
    desktopBackgroundImageAltText: desktopBackgroundImageAltText,
    productsSubsection: buildProductSubsection(
      contentfulSection,
      productsWithSpreeData,
    ),
  };

  return BannerAndCardsV2Props;
};
